import { apiClient } from '../services/api';
import { API_BASE_URL } from '../config';
import { guardarCodigo, limpiarCodigo } from './closerCode';

/**
 * El enlace de referido por el que llegó el visitante: de un closer o de un canal.
 *
 * Decide dos cosas (Ricardo, 16-sep-2026):
 *   · si se le ofrece el beneficio de bienvenida — solo si el enlace lo tiene
 *     encendido;
 *   · a quién se le cuenta cuando se registre.
 *
 * Se guarda 30 días en `localStorage`, por la misma razón que el código del
 * closer: los enlaces llegan por WhatsApp o por un QR, y el navegador interno de
 * esas apps se cierra y se reabre.
 *
 * Esto es presentación y atribución, no la puerta. Quién puede desbloquear el
 * gratis lo decide el servidor con lo que quedó registrado en la cuenta.
 */

const LLAVE = 'vondi.enlace';
const DIAS_DE_VIDA = 30;

/** Aviso de que el enlace ya se resolvió; las pantallas lo esperan. */
export const EVENTO_ENLACE = 'vondi:enlace';

export function leerEnlace() {
  try {
    const dato = JSON.parse(localStorage.getItem(LLAVE) || 'null');
    if (!dato?.codigo) return null;
    if ((Date.now() - (dato.guardado ?? 0)) / 86400000 > DIAS_DE_VIDA) {
      localStorage.removeItem(LLAVE);
      return null;
    }
    return dato;
  } catch (e) {
    return null;
  }
}

function guardarEnlace(datos) {
  try {
    localStorage.setItem(LLAVE, JSON.stringify({ ...datos, guardado: Date.now() }));
  } catch (e) {
    /* Modo privado o almacenamiento lleno: se sigue sin enlace. */
  }

  /*
    El código del closer se sigue guardando aparte porque de él vive el cobro
    (el descuento y la comisión). Y si el último enlace fue de un canal, se
    borra: gana el último enlace que abrió la persona, y un closer anterior no
    debe cobrar comisión por alguien que llegó después por un QR del outlet.
  */
  if (datos.tipo === 'closer') {
    guardarCodigo({ code: datos.codigo, discount_percent: datos.descuento, closer_name: datos.nombre });
  } else {
    limpiarCodigo();
  }

  window.dispatchEvent(new CustomEvent(EVENTO_ENLACE));
}

/**
 * Resuelve un código contra el servidor y, si es válido, lo guarda.
 *
 * La visita se cuenta una sola vez por enlace y navegador: recargar la página
 * no debe inflar los números de nadie.
 *
 * @returns {Promise<object|null>} el enlace, o null si el código no es válido.
 */
export async function capturarEnlace(codigo) {
  const limpio = String(codigo || '').trim().replace(/^5-/, '').toUpperCase();
  if (!limpio) return null;

  const llaveVisita = `vondi.visita.${limpio}`;
  let yaContada = false;
  try { yaContada = Boolean(localStorage.getItem(llaveVisita)); } catch (e) { /* sin almacenamiento */ }

  try {
    const r = await apiClient.get(`${API_BASE_URL}/api/enlaces/${encodeURIComponent(limpio)}`, {
      registrar: yaContada ? 0 : 1,
    });
    const datos = r?.data;
    if (!r?.success || !datos?.valid) return null;

    try { localStorage.setItem(llaveVisita, '1'); } catch (e) { /* sin almacenamiento */ }

    const enlace = {
      tipo: datos.tipo,
      codigo: datos.codigo,
      nombre: datos.nombre,
      da_beneficio: Boolean(datos.da_beneficio),
      descuento: datos.descuento,
    };
    guardarEnlace(enlace);
    return enlace;
  } catch (e) {
    /* Un código inválido o el servidor caído no deben estropear la visita. */
    return null;
  }
}

/** ¿La URL actual trae un enlace que todavía no se ha resuelto? */
export function hayEnlaceEnLaUrl() {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname.startsWith('/r/')) return true;
  const q = new URLSearchParams(window.location.search);
  return Boolean(q.get('code') || q.get('ref') || q.get('utm_source'));
}
